import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { authApi, organizationsApi, isProduction } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';

export default function Login() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, setCurrentOrganization, isAuthenticated, user, isLoading: authLoading } = useAuthStore();
  const navigate = useNavigate();
  const redirectPath = searchParams.get('redirect');

  // Track if login was just performed to avoid race condition with useEffect
  const justLoggedIn = useRef(false);

  // Redirect already logged-in users (only for users who were ALREADY logged in on page load)
  useEffect(() => {
    if (authLoading) return;

    // Skip if user just logged in via form - handleSubmit will handle the redirect
    if (justLoggedIn.current) return;

    if (isAuthenticated && user) {
      // If there's a redirect path, go there
      if (redirectPath) {
        navigate(redirectPath, { replace: true });
        return;
      }
      // Otherwise, redirect to appropriate dashboard
      if (user.role === 'superadmin') {
        navigate('/admin', { replace: true });
      } else if (user.role === 'org_admin') {
        navigate('/org', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [authLoading, isAuthenticated, user, redirectPath, navigate]);

  // Show loading while checking auth state
  if (authLoading) {
    return (
      <div className="animate-fade-in flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-rust"></div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await authApi.login(email, password);

      if (response.data.success) {
        const { user, tokens } = response.data.data;
        // Mark that we just logged in so useEffect doesn't also try to redirect
        justLoggedIn.current = true;
        login(user, tokens);

        if (user.role === 'org_admin' && user.organizationId) {
          try {
            const orgResponse = await organizationsApi.get(user.organizationId);
            if (orgResponse.data.success) {
              setCurrentOrganization(orgResponse.data.data.organization);
            }
          } catch {
            // Continue without organization
          }
        }

        toast.success('Welcome back!');

        // Check for redirect path (e.g., from invite flow)
        if (redirectPath) {
          navigate(redirectPath, { replace: true });
        } else if (user.role === 'superadmin') {
          navigate('/admin', { replace: true });
        } else if (user.role === 'org_admin') {
          navigate('/org', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: { message?: string; code?: string } } } };
      const errorMessage = err.response?.data?.error?.message || 'Login failed';
      const errorCode = err.response?.data?.error?.code;

      if (errorCode === 'USER_NOT_CONFIRMED' || errorMessage.toLowerCase().includes('verify your email')) {
        toast.error('Please verify your email first');
        navigate(`/verify-email?email=${encodeURIComponent(email)}`);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="lg:hidden mb-8 text-center">
        <Link to="/" className="font-display text-2xl font-bold text-charcoal tracking-tight">
          venndela
        </Link>
        <p className="text-charcoal/60 mt-1">Turn customers into advocates</p>
      </div>

      <h2 className="font-serif text-3xl text-charcoal mb-2">Welcome back</h2>
      <p className="text-charcoal/60 mb-8">Sign in to your account</p>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="email" className="label">
            Email address
          </label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="input"
            placeholder="you@example.com"
            required
          />
        </div>

        <div>
          <div className="flex items-center justify-between">
            <label htmlFor="password" className="label">
              Password
            </label>
            <Link
              to="/forgot-password"
              className="text-sm text-rust hover:text-rust-light"
            >
              Forgot password?
            </Link>
          </div>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input"
            placeholder="Enter your password"
            required
          />
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="btn btn-primary w-full py-3"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Signing in...
            </span>
          ) : (
            'Sign in'
          )}
        </button>
      </form>

      <p className="mt-8 text-center text-sm text-charcoal/60">
        Don't have an account?{' '}
        <Link to={isProduction() ? '/coming-soon' : '/register'} className="text-rust hover:text-rust-light font-medium">
          Create an account
        </Link>
      </p>
    </div>
  );
}
